import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, ArrowUp, ArrowDown, ArrowUpDown, Infinity as InfinityIcon } from 'lucide-react';
import { iniciales, tiempoRelativo } from './utils/helpers';

// Template compartido entre header y filas — alinea ambos al pixel.
const COL_TEMPLATE = 'grid-cols-[minmax(0,1fr)_220px_140px_100px_36px]';

// ---------- Header con sort ----------

const SortHeader = ({ col, label, orden, setOrden }) => {
  const active = orden.startsWith(`${col}_`);
  const desc = orden === `${col}_desc`;
  const handle = () => {
    if (!active) setOrden(`${col}_asc`);
    else setOrden(desc ? `${col}_asc` : `${col}_desc`);
  };
  return (
    <button
      onClick={handle}
      className={`group/sort inline-flex items-center gap-1 text-[10px] font-semibold tracking-widest uppercase transition-colors w-fit ${
        active ? 'text-slate-700' : 'text-slate-400 hover:text-slate-600'
      }`}
    >
      <span>{label}</span>
      {active ? (
        desc
          ? <ArrowDown size={11} strokeWidth={2.5} />
          : <ArrowUp size={11} strokeWidth={2.5} />
      ) : (
        <ArrowUpDown
          size={11}
          className="opacity-0 group-hover/sort:opacity-50 transition-opacity"
        />
      )}
    </button>
  );
};

const ListHeader = ({ orden, setOrden }) => (
  <div className={`sticky top-0 z-10 grid ${COL_TEMPLATE} gap-4 items-center px-4 py-2.5 bg-white border-b border-slate-200/70`}>
    <SortHeader col="nombre" label="Proyecto" orden={orden} setOrden={setOrden} />
    <SortHeader col="progreso" label="Progreso" orden={orden} setOrden={setOrden} />
    <div className="text-[10px] font-semibold tracking-widest uppercase text-slate-300">
      Equipo
    </div>
    <SortHeader col="actividad" label="Actividad" orden={orden} setOrden={setOrden} />
    <div />
  </div>
);

// ---------- Fila ----------

const ListRow = ({ proyecto, index, onToggleFavorito, filtro }) => {
  const color = proyecto.color || '#06b6d4';
  const total = proyecto.stats?.total ?? 0;
  const hechas = proyecto.stats?.hechas ?? 0;
  const activas = proyecto.stats?.activas ?? Math.max(total - hechas, 0);
  const progreso = total > 0 ? Math.round((hechas / total) * 100) : 0;
  const esContinuo = !!proyecto.es_continuo;
  const miembros = (proyecto.miembros || []).slice(0, 3);
  const extra = (proyecto.miembros?.length || 0) - miembros.length;
  // Cap del stagger igual que en grid: con muchas filas no queremos esperar mucho.
  const delay = Math.min(index * 0.015, 0.18);

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
    >
      <Link
        to={`/proyectos/${proyecto.slug}`}
        className={`group grid ${COL_TEMPLATE} gap-4 items-center px-4 py-3 hover:bg-slate-50 focus-visible:bg-slate-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-cyan-200 transition-colors`}
      >
        {/* Col 1: dot + nombre + slug + (chip archivado) */}
        <div className="flex items-center gap-3 min-w-0">
          <span
            className="shrink-0 w-2.5 h-2.5 rounded-full transition-transform group-hover:scale-110"
            style={{ backgroundColor: color }}
          />
          <div className="flex items-baseline gap-2 min-w-0">
            <span className="truncate text-sm font-medium text-slate-900">
              {proyecto.nombre}
            </span>
            <span className="shrink-0 font-mono text-[10px] tracking-widest text-slate-400 uppercase">
              {proyecto.slug}
            </span>
            {/* Solo cuando NO estás filtrando archivados — si ya filtraste, el chip es redundante. */}
            {proyecto.archivado && filtro !== 'archivados' && (
              <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium tracking-wider uppercase bg-slate-100 text-slate-500">
                Archivado
              </span>
            )}
          </div>
        </div>

        {/* Col 2: progreso o chip "Continuo" (proyectos sin fecha de fin) */}
        {esContinuo ? (
          <div className="flex items-center gap-2 min-w-0">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium uppercase tracking-wider bg-cyan-50 text-cyan-700 shrink-0">
              <InfinityIcon size={10} strokeWidth={2.5} />
              Continuo
            </span>
            <span className="text-[11px] text-slate-500 tabular-nums truncate">
              {activas} {activas === 1 ? 'activa' : 'activas'}
            </span>
          </div>
        ) : (
          <div
            className="flex items-center gap-2.5"
            title={total > 0 ? `${hechas} de ${total} ${total === 1 ? 'tarea hecha' : 'tareas hechas'}` : 'Sin tareas'}
          >
            <div className="flex-1 h-1 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-[width] duration-300"
                style={{ width: `${progreso}%`, backgroundColor: color }}
              />
            </div>
            <span className="shrink-0 w-9 text-right text-[11px] font-medium text-slate-700 tabular-nums">
              {progreso}%
            </span>
          </div>
        )}

        {/* Col 3: miembros */}
        <div className="flex items-center -space-x-1.5">
          {miembros.map((m) => (
            <div
              key={m.empleado_id}
              title={m.nombre}
              className="w-6 h-6 rounded-full ring-2 ring-white text-[10px] font-semibold flex items-center justify-center"
              style={{ background: `linear-gradient(${color}20, ${color}20), #fff`, color }}
            >
              {iniciales(m.nombre)}
            </div>
          ))}
          {extra > 0 && (
            <div className="w-6 h-6 rounded-full ring-2 ring-white bg-slate-50 text-slate-500 text-[10px] font-medium flex items-center justify-center">
              +{extra}
            </div>
          )}
          {miembros.length === 0 && (
            <span className="text-[11px] text-slate-300">—</span>
          )}
        </div>

        {/* Col 4: actividad */}
        <div className="text-[11px] text-slate-400 tabular-nums">
          {tiempoRelativo(proyecto.updated_at)}
        </div>

        {/* Col 5: favorito (oculto hasta hover si no es favorito) */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggleFavorito(proyecto);
          }}
          className={`p-1 -m-1 rounded justify-self-end transition-all active:scale-90 ${
            proyecto.es_favorito
              ? 'text-amber-400 opacity-100'
              : 'text-slate-300 opacity-0 group-hover:opacity-100 hover:text-amber-400'
          }`}
          title={proyecto.es_favorito ? 'Quitar de favoritos' : 'Marcar como favorito'}
        >
          <Star
            size={14}
            strokeWidth={2}
            className={proyecto.es_favorito ? 'fill-amber-400' : ''}
          />
        </button>
      </Link>
    </motion.div>
  );
};

// ---------- Skeleton ----------

const ListSkeleton = () => (
  <div className="divide-y divide-slate-100">
    {[...Array(8)].map((_, i) => (
      <div
        key={i}
        className={`grid ${COL_TEMPLATE} gap-4 items-center px-4 py-3 skeleton`}
      >
        <div className="flex items-center gap-3">
          <div className="w-2.5 h-2.5 rounded-full bg-slate-200" />
          <div className="h-3 w-48 bg-slate-100 rounded" />
        </div>
        <div className="h-1 bg-slate-100 rounded-full" />
        <div className="flex -space-x-1.5">
          <div className="w-6 h-6 rounded-full bg-slate-100 ring-2 ring-white" />
          <div className="w-6 h-6 rounded-full bg-slate-100 ring-2 ring-white" />
        </div>
        <div className="h-2.5 w-14 bg-slate-100 rounded" />
        <div />
      </div>
    ))}
  </div>
);

// ---------- Vista lista ----------

const ProyectosListView = ({
  proyectos,
  orden,
  setOrden,
  onToggleFavorito,
  loading,
  filtro,
}) => (
  <div className="bg-white rounded-xl border border-slate-200/70 overflow-hidden">
    <ListHeader orden={orden} setOrden={setOrden} />
    {loading ? (
      <ListSkeleton />
    ) : (
      <div className="divide-y divide-slate-100">
        {proyectos.map((p, i) => (
          <ListRow
            key={p.id}
            proyecto={p}
            index={i}
            onToggleFavorito={onToggleFavorito}
            filtro={filtro}
          />
        ))}
      </div>
    )}
  </div>
);

export default ProyectosListView;
